import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  LucideCircleAlert,
  LucideCircleCheck,
  LucideDynamicIcon,
  LucideInfo,
  LucideTriangleAlert,
  LucideX,
  provideLucideIcons,
} from '@lucide/angular';
import { ToastService, ToastType } from '../../core/services/toast.service';

@Component({
  selector: 'ui-toast-host',
  standalone: true,
  imports: [CommonModule, LucideDynamicIcon],
  providers: [
    provideLucideIcons(
      LucideCircleCheck,
      LucideCircleAlert,
      LucideTriangleAlert,
      LucideInfo,
      LucideX,
    ),
  ],
  template: `
    <div class="ui-toast-host" aria-live="polite" aria-relevant="additions">
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          class="ui-toast"
          role="alert"
          [class.ui-toast--success]="toast.type === 'success'"
          [class.ui-toast--error]="toast.type === 'error'"
          [class.ui-toast--warning]="toast.type === 'warning'"
          [class.ui-toast--info]="toast.type === 'info'">
          <span class="ui-toast__icon-wrap" aria-hidden="true">
            <svg
              class="ui-toast__icon"
              [lucideIcon]="iconFor(toast.type)"
              [size]="18"
              strokeWidth="2.25"></svg>
          </span>
          <div class="ui-toast__body">
            <span class="ui-toast__label">{{ labelFor(toast.type) }}</span>
            <p class="ui-toast__message">{{ toast.message }}</p>
          </div>
          <button
            type="button"
            class="ui-toast__close"
            aria-label="Fechar notificação"
            (click)="dismiss($event, toast.id)">
            <svg lucideIcon="x" [size]="14" strokeWidth="2.25" aria-hidden="true"></svg>
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    .ui-toast-host {
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 0.65rem;
      width: min(22rem, calc(100vw - 2rem));
      pointer-events: none;
    }

    .ui-toast {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.85rem 0.75rem 0.85rem 0.85rem;
      border-radius: 0.875rem;
      border: 1px solid var(--border-color, #e8e8e8);
      background: var(--surface-bg, #fff);
      box-shadow:
        0 10px 28px rgba(15, 23, 42, 0.12),
        0 2px 8px rgba(15, 23, 42, 0.06);
      pointer-events: auto;
      animation: ui-toast-enter 0.28s cubic-bezier(0.22, 1, 0.36, 1);
    }

    @keyframes ui-toast-enter {
      from {
        opacity: 0;
        transform: translateX(1.1rem) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateX(0) scale(1);
      }
    }

    .ui-toast__icon-wrap {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      flex-shrink: 0;
      border-radius: 999px;
    }

    .ui-toast__icon {
      flex-shrink: 0;
    }

    .ui-toast__body {
      flex: 1;
      min-width: 0;
      padding-top: 0.05rem;
    }

    .ui-toast__label {
      display: block;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      line-height: 1.2;
      margin-bottom: 0.15rem;
    }

    .ui-toast__message {
      margin: 0;
      font-size: 0.8125rem;
      line-height: 1.45;
      color: var(--text-body, #434343);
      word-break: break-word;
    }

    .ui-toast__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      margin-top: -0.1rem;
      flex-shrink: 0;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--text-muted, #8c8c8c);
      cursor: pointer;
      transition: background 0.15s, color 0.15s;
    }

    .ui-toast__close:hover {
      background: var(--surface-muted, #f5f5f5);
      color: var(--text-body, #434343);
    }

    .ui-toast__close:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-primary, #1677ff) 28%, transparent);
    }

    .ui-toast--success {
      border-color: color-mix(in srgb, #52c41a 28%, var(--border-color, #e8e8e8));
      background: linear-gradient(
        135deg,
        color-mix(in srgb, #52c41a 7%, var(--surface-bg, #fff)) 0%,
        var(--surface-bg, #fff) 55%
      );
    }

    .ui-toast--success .ui-toast__icon-wrap {
      background: color-mix(in srgb, var(--success, #52c41a) 14%, var(--surface-bg));
      color: var(--success, #52c41a);
    }

    .ui-toast--success .ui-toast__label {
      color: var(--success, #52c41a);
    }

    .ui-toast--error {
      border-color: color-mix(in srgb, #ff4d4f 28%, var(--border-color, #e8e8e8));
      background: linear-gradient(
        135deg,
        color-mix(in srgb, #ff4d4f 7%, var(--surface-bg, #fff)) 0%,
        var(--surface-bg, #fff) 55%
      );
    }

    .ui-toast--error .ui-toast__icon-wrap {
      background: color-mix(in srgb, var(--danger, #ff4d4f) 14%, var(--surface-bg));
      color: var(--danger, #ff4d4f);
    }

    .ui-toast--error .ui-toast__label {
      color: var(--danger, #ff4d4f);
    }

    .ui-toast--warning {
      border-color: color-mix(in srgb, #faad14 30%, var(--border-color, #e8e8e8));
      background: linear-gradient(
        135deg,
        color-mix(in srgb, #faad14 8%, var(--surface-bg, #fff)) 0%,
        var(--surface-bg, #fff) 55%
      );
    }

    .ui-toast--warning .ui-toast__icon-wrap {
      background: color-mix(in srgb, var(--warning, #faad14) 16%, var(--surface-bg));
      color: var(--warning, #faad14);
    }

    .ui-toast--warning .ui-toast__label {
      color: var(--warning, #faad14);
    }

    .ui-toast--info {
      border-color: color-mix(in srgb, var(--brand-primary, #1677ff) 28%, var(--border-color, #e8e8e8));
      background: linear-gradient(
        135deg,
        color-mix(in srgb, var(--brand-primary, #1677ff) 7%, var(--surface-bg, #fff)) 0%,
        var(--surface-bg, #fff) 55%
      );
    }

    .ui-toast--info .ui-toast__icon-wrap {
      background: color-mix(in srgb, var(--brand-primary, #1677ff) 14%, var(--surface-bg));
      color: var(--brand-primary, #1677ff);
    }

    .ui-toast--info .ui-toast__label {
      color: var(--brand-primary, #1677ff);
    }
  `],
})
export class UiToastHostComponent {
  toastService = inject(ToastService);

  iconFor(type: ToastType): string {
    const map: Record<ToastType, string> = {
      success: 'circle-check',
      error: 'circle-alert',
      warning: 'triangle-alert',
      info: 'info',
    };
    return map[type];
  }

  labelFor(type: ToastType): string {
    const map: Record<ToastType, string> = {
      success: 'Sucesso',
      error: 'Erro',
      warning: 'Atenção',
      info: 'Informação',
    };
    return map[type];
  }

  dismiss(event: Event, id: number): void {
    event.stopPropagation();
    this.toastService.dismiss(id);
  }
}
